/**
 * Demo initialization system
 * Handles setup and configuration of the demo environment
 */

import { configManager } from '../config';
import { assetManager } from './assetManager';
import { featureFlags } from './featureFlags';

export interface DemoInitializationResult {
  success: boolean;
  errors: string[];
  warnings: string[];
  timing: {
    configLoad: number;
    assetPreload: number;
    featureSetup: number;
    total: number;
  };
}

class DemoInitializer {
  private initialized = false;
  private initializationPromise: Promise<DemoInitializationResult> | null = null;

  /**
   * Initialize the demo environment
   */
  async initialize(): Promise<DemoInitializationResult> {
    if (this.initialized) {
      return {
        success: true,
        errors: [],
        warnings: ['Demo already initialized'],
        timing: { configLoad: 0, assetPreload: 0, featureSetup: 0, total: 0 },
      };
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  /**
   * Perform the actual initialization
   */
  private async performInitialization(): Promise<DemoInitializationResult> {
    const startTime = performance.now();
    const errors: string[] = [];
    const warnings: string[] = [];
    const timing = { configLoad: 0, assetPreload: 0, featureSetup: 0, total: 0 };

    try {
      // Step 1: Load and validate configuration
      const configStart = performance.now();
      await this.initializeConfiguration();
      timing.configLoad = performance.now() - configStart;

      // Step 2: Set up feature flags
      const featureStart = performance.now();
      await this.initializeFeatureFlags();
      timing.featureSetup = performance.now() - featureStart;

      // Step 3: Preload critical assets
      const assetStart = performance.now();
      await this.initializeAssets();
      timing.assetPreload = performance.now() - assetStart;

      // Step 4: Set up demo-specific environment
      await this.setupDemoEnvironment();

      // Step 5: Initialize mock services if needed
      await this.initializeMockServices();

      // Step 6: Set up error handling
      this.setupErrorHandling();

      // Step 7: Set up performance monitoring
      this.setupPerformanceMonitoring();

      timing.total = performance.now() - startTime;
      this.initialized = true;

      console.log('Demo initialization completed successfully', {
        timing,
        config: configManager.config,
      });

      return {
        success: true,
        errors,
        warnings,
        timing,
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error';
      errors.push(errorMessage);
      
      timing.total = performance.now() - startTime;

      console.error('Demo initialization failed:', error);

      return {
        success: false,
        errors,
        warnings,
        timing,
      };
    }
  }

  /**
   * Initialize and validate configuration
   */
  private async initializeConfiguration(): Promise<void> {
    const validation = configManager.validate();
    
    if (!validation.valid) {
      throw new Error(`Configuration validation failed: ${validation.errors.join(', ')}`);
    }

    // Log configuration in debug mode
    if (configManager.config.env.debugMode) {
      console.log('Demo configuration loaded:', configManager.getDebugInfo());
    }
  }

  /**
   * Initialize feature flags
   */
  private async initializeFeatureFlags(): Promise<void> {
    const config = configManager.config;
    
    // Set feature flags based on configuration
    Object.entries(config.demo.features).forEach(([flag, enabled]) => {
      if (enabled) {
        featureFlags.enable(flag as any);
      } else {
        featureFlags.disable(flag as any);
      }
    });

    // Override with environment-specific flags
    const envFlags = [
      'providerFailover',
      'settlementRailDemo',
      'costCalculator',
      'professionalBranding',
      'demoControlPanel',
      'offlineMode',
    ];

    envFlags.forEach(flag => {
      const envValue = configManager.getFeatureFlag(flag);
      if (envValue !== config.demo.features[flag as keyof typeof config.demo.features]) {
        if (envValue) {
          featureFlags.enable(flag as any);
        } else {
          featureFlags.disable(flag as any);
        }
      }
    });

    console.log('Feature flags initialized:', featureFlags.getAllFeatures());
  }

  /**
   * Initialize and preload assets
   */
  private async initializeAssets(): Promise<void> {
    const config = configManager.getPerformanceConfig();
    
    if (config.preloadCriticalAssets) {
      await assetManager.preloadCriticalAssets();
    }

    // Set up favicon
    this.setupFavicon();

    // Set up meta tags
    this.setupMetaTags();
  }

  /**
   * Set up demo-specific environment
   */
  private async setupDemoEnvironment(): Promise<void> {
    const config = configManager.config;
    
    // Set up document title
    document.title = 'StablePay - Professional Payment Platform Demo';

    // Add demo-specific CSS classes to body
    document.body.classList.add('demo-mode');
    document.body.classList.add(`theme-${config.demo.assets.theme}`);

    // Set up CSS custom properties for theming
    this.setupCSSCustomProperties();

    // Set up offline mode if enabled
    if (featureFlags.isEnabled('offlineMode')) {
      this.setupOfflineMode();
    }
  }

  /**
   * Initialize mock services
   */
  private async initializeMockServices(): Promise<void> {
    const config = configManager.config;
    
    if (config.env.enableMockApi) {
      // Import and initialize mock API service
      const { mockApiService } = await import('../services/mockApiService');
      await mockApiService.initialize();
      
      console.log('Mock API service initialized');
    }
  }

  /**
   * Set up error handling
   */
  private setupErrorHandling(): void {
    // Global error handler
    window.addEventListener('error', (event) => {
      console.error('Global error:', event.error);
      this.handleError(event.error);
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      this.handleError(event.reason);
    });
  }

  /**
   * Set up performance monitoring
   */
  private setupPerformanceMonitoring(): void {
    const config = configManager.config;
    
    if (config.env.debugMode) {
      // Monitor performance metrics
      if ('performance' in window && 'observe' in window.performance) {
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            console.log('Performance entry:', entry);
          });
        });
        
        observer.observe({ entryTypes: ['navigation', 'resource', 'measure'] });
      }
    }
  }

  /**
   * Set up favicon
   */
  private setupFavicon(): void {
    const faviconUrl = assetManager.getAssetUrl('logos', 'favicon');
    
    // Update existing favicon or create new one
    let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    
    favicon.href = faviconUrl;
  }

  /**
   * Set up meta tags
   */
  private setupMetaTags(): void {
    const metaTags = [
      { name: 'description', content: 'StablePay Professional Payment Platform Demo - Multi-stablecoin payments with instant settlement' },
      { name: 'keywords', content: 'stablecoin, payments, Hong Kong, USDT, USDC, settlement, FPS, CIPS' },
      { name: 'author', content: 'StablePay' },
      { property: 'og:title', content: 'StablePay - Professional Payment Platform' },
      { property: 'og:description', content: 'Experience the future of cross-border payments with multi-stablecoin support and instant settlement' },
      { property: 'og:type', content: 'website' },
    ];

    metaTags.forEach(({ name, property, content }) => {
      const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (name) meta.name = name;
        if (property) meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      
      meta.content = content;
    });
  }

  /**
   * Set up CSS custom properties
   */
  private setupCSSCustomProperties(): void {
    const assetConfig = configManager.getAssetConfig();
    const logoUrl = assetManager.getLogoUrl('full');
    
    document.documentElement.style.setProperty('--logo-url', `url(${logoUrl})`);
    document.documentElement.style.setProperty('--theme', assetConfig.theme);
    document.documentElement.style.setProperty('--logo-variant', assetConfig.logoVariant);
  }

  /**
   * Set up offline mode
   */
  private setupOfflineMode(): void {
    // Register service worker for offline capabilities
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/demo-sw.js')
        .then((registration) => {
          console.log('Service worker registered for offline mode:', registration);
        })
        .catch((error) => {
          console.warn('Service worker registration failed:', error);
        });
    }

    // Listen for online/offline events
    window.addEventListener('online', () => {
      document.body.classList.remove('offline-mode');
      console.log('Demo back online');
    });

    window.addEventListener('offline', () => {
      document.body.classList.add('offline-mode');
      console.log('Demo in offline mode');
    });
  }

  /**
   * Handle errors
   */
  private handleError(error: any): void {
    const config = configManager.config;
    
    if (config.env.debugMode) {
      // Show error in development
      console.error('Demo error:', error);
    } else {
      // Log error silently in production
      console.warn('An error occurred in the demo');
    }

    // Could send to error tracking service here
  }

  /**
   * Reset demo to initial state
   */
  async reset(): Promise<void> {
    // Reset feature flags
    featureFlags.resetAll();
    
    // Clear asset cache
    assetManager.clearCache();
    
    // Reset configuration
    configManager.reload();
    
    // Re-initialize
    this.initialized = false;
    this.initializationPromise = null;
    
    await this.initialize();
    
    console.log('Demo reset completed');
  }

  /**
   * Get initialization status
   */
  getStatus(): {
    initialized: boolean;
    config: any;
    features: Record<string, boolean>;
    assets: { size: number; keys: string[] };
  } {
    return {
      initialized: this.initialized,
      config: configManager.config,
      features: featureFlags.getAllFeatures(),
      assets: assetManager.getCacheStats(),
    };
  }
}

// Singleton instance
export const demoInitializer = new DemoInitializer();

// Auto-initialize on import in demo mode
const config = configManager.config;
if (config.runtime.isDemo || config.env.enableMockApi) {
  demoInitializer.initialize().catch(console.error);
}